import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyOtp as verifyOtpApi, VerifyOtpData } from '@api/auth';
import { axiosIns } from '../api/index';
import { getProfileDetails } from '../api/profile';
import { ApiResponse } from '../typings/ApiResponse';
import { ProfileData } from '../typings/ProfileTypes';

interface AuthContextType {
  isLoggedIn: boolean;
  profile: ProfileData | null;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verifyOtp: (uuid: string, otp: string) => Promise<ApiResponse<VerifyOtpData>>;
  logout: () => Promise<void>;
  partnerId: string | null;
  switchPartner: (id: string) => void;
}

interface LoginResponse {
  code: number;
  data: any;
  message: string;
  response_time: string;
  status: string;
  user_message: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => !!localStorage.getItem('authToken')
  );
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(
    localStorage.getItem('partnerId')
  );
  const navigate = useNavigate();

  const fetchProfile = useCallback(async () => {
    try {
      const getProfileResponse: ApiResponse<ProfileData> =
        await getProfileDetails();
      setProfile(getProfileResponse.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch profile details:', error);
    }
  }, []);

  const switchPartner = useCallback(
    (id: string) => {
      setPartnerId(id);
      localStorage.setItem('partnerId', id);
    },
    [setPartnerId]
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const response = await axiosIns.post<LoginResponse>(
        '/carehome/auth/login-v1',
        { email, password }
      );
      const { data } = response;
      if (data?.status === 'success') {
        const { uuid } = data.data;
        localStorage.setItem('uuid', uuid);
        navigate(`/confirmation/${uuid}`);
      }
      return data;
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setProfile(null);
    axiosIns.defaults.headers.common.Authorization = undefined;
  }, []);

  const verifyOtp = useCallback(
    async (uuid: string, otp: string): Promise<ApiResponse<VerifyOtpData>> => {
      const response = await verifyOtpApi(uuid, otp);

      if (response?.status === 'success') {
        const { token } = response.data;

        localStorage.setItem('authToken', token);
        localStorage.removeItem('uuid');

        setIsLoggedIn(true);
        axiosIns.defaults.headers.common.Authorization = `Bearer ${token}`;

        await fetchProfile();
        navigate('/', { replace: true });
      }

      return response;
    },
    [fetchProfile, navigate]
  );

  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'authToken' && !event.newValue) logout();
    };
    window.addEventListener('storage', syncLogout);
    return () => window.removeEventListener('storage', syncLogout);
  }, [logout]);

  useEffect(() => {
    if (isLoggedIn && !profile) {
      fetchProfile();
    }
  }, [isLoggedIn, profile, fetchProfile]);

  const authContextProps = useMemo(
    () => ({
      isLoggedIn,
      profile,
      login,
      verifyOtp,
      logout,
      switchPartner,
      partnerId,
    }),
    [isLoggedIn, login, verifyOtp, profile, logout, partnerId, switchPartner]
  );

  return (
    <AuthContext.Provider value={authContextProps}>
      {children}
    </AuthContext.Provider>
  );
};
